// ===============================================
// File: src/components/Settings/StoragePanel.jsx
// Description: Simple storage controls panel for folder/drive,
// with backup + restore actions.
// ===============================================

import React, { useRef, useState } from "react";
import { useStorage } from "@/contexts/StorageContext";

export default function StoragePanel() {
  const {
    mode,
    hasFolder,
    baseDirName,
    workspaceName,

    // folder/drive link
    pickFolder,
    clearFolderSelection,
    connectDrive,
    disconnectDrive,

    // file-level save/receive (domain files)
    masterSaveFiles,
    masterReceiveFiles,

    // vault snapshot send/receive (bundle)
    saveAll,
    forceSend,
    reload,

    // backup + health
    exportAll,
    importBackup,
    healthCheck,

    // workspace state
    hasWorkspace,
    activeWorkspace,
  } = useStorage();

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  async function run(fn, label) {
    setBusy(true);
    setMsg(label ? `${label}…` : "Working…");
    try {
      const res = await fn();
      setMsg(JSON.stringify(res));
    } catch (e) {
      console.error(e);
      setMsg(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleImportFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setMsg("Importing backup…");
    try {
      const res = await importBackup(f);
      setMsg(`Imported ${res.keys ?? 0} keys from backup`);
    } catch (e) {
      console.error(e);
      setMsg(e?.message || String(e));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  }

  const onDisconnectDrive = async () => {
    const confirmed = confirm(
      "⚠️ Disconnect from Google Drive?\n\n" +
      "This will immediately clear all data from the app. " +
      "Your workspace data will remain in Google Drive and can be reconnected later."
    );
    if (!confirmed) return;
    await run(disconnectDrive, "Disconnecting Drive");
  };

  const onForgetFolder = async () => {
    const confirmed = confirm(
      "⚠️ Forget local folder?\n\n" +
      "This will immediately clear all data from the app. " +
      "Your workspace data will remain in the folder and can be reconnected later."
    );
    if (!confirmed) return;
    await run(clearFolderSelection, "Clearing folder link");
  };

  return (
    <div className="max-w-3xl mx-auto p-4 text-sm">
      <h1 className="text-xl font-semibold mb-4">Storage</h1>

      {/* Workspace Status Alert */}
      {!hasWorkspace && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm">
          ⚠️ <strong>No workspace selected.</strong> Connect a workspace below to load and save your studio data.
        </div>
      )}
      {hasWorkspace && activeWorkspace && (
        <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-200 text-sm">
          📁 <strong>Active Workspace:</strong> {activeWorkspace.type === "drive" ? "Google Drive" : "Local Folder"} 
          {activeWorkspace.name && ` (${activeWorkspace.name})`}
        </div>
      )}

      <section className="mb-6 rounded-lg border border-white/10 p-4 bg-white/5">
        <div className="mb-2">
          <div className="opacity-70">Current workspace</div>
          <div className="text-white">
            Mode: <b>{mode || "none"}</b>{" "}
            {hasFolder ? (
              <>
                • Folder: <b>{baseDirName}</b>
              </>
            ) : null}{" "}
            {workspaceName ? <>• {workspaceName}</> : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-8">
          <div>
            <div className="font-medium mb-2">Local folder</div>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 rounded bg-blue-600/80 hover:bg-blue-600 disabled:opacity-50"
                onClick={() => run(pickFolder, "Selecting folder")}
                disabled={busy}
              >
                Link Folder…
              </button>
              <button
                className="px-3 py-1.5 rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50"
                onClick={onForgetFolder}
                disabled={busy}
              >
                Unlink
              </button>
            </div>
          </div>

          <div>
            <div className="font-medium mb-2">Google Drive</div>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 rounded bg-emerald-600/80 hover:bg-emerald-600 disabled:opacity-50"
                onClick={() => run(connectDrive, "Connecting Drive")}
                disabled={busy}
              >
                Connect Drive
              </button>
              <button
                className="px-3 py-1.5 rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50"
                onClick={onDisconnectDrive}
                disabled={busy}
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-6 rounded-lg border border-white/10 p-4 bg-white/5">
        <h2 className="font-medium mb-3">File-level Save/Receive (domain files)</h2>
        <div className="flex flex-wrap gap-2">
          <button
            className="px-3 py-1.5 rounded bg-orange-600/80 hover:bg-orange-600 disabled:opacity-50"
            onClick={() => run(masterSaveFiles, "Master Save (Files)")}
            disabled={busy || !hasWorkspace}
          >
            Master Save (Files)
          </button>
          <button
            className="px-3 py-1.5 rounded bg-indigo-600/80 hover:bg-indigo-600 disabled:opacity-50"
            onClick={() => run(() => masterReceiveFiles({ replaceMode: true }), "Master Receive (Files)")}
            disabled={busy || !hasWorkspace}
          >
            Master Receive (Replace)
          </button>
          <button
            className="px-3 py-1.5 rounded bg-indigo-600/60 hover:bg-indigo-600 disabled:opacity-50"
            onClick={() => run(() => masterReceiveFiles({ replaceMode: false }), "Master Receive (Merge)")}
            disabled={busy || !hasWorkspace}
          >
            Master Receive (Merge)
          </button>
        </div>
      </section>

      <section className="mb-6 rounded-lg border border-white/10 p-4 bg-white/5">
        <h2 className="font-medium mb-3">Vault snapshot (bundle) Send/Receive</h2>
        <div className="flex flex-wrap gap-2">
          <button
            className="px-3 py-1.5 rounded bg-sky-600/80 hover:bg-sky-600 disabled:opacity-50"
            onClick={() => run(saveAll, "Send (bundle)")}
            disabled={busy || !hasWorkspace}
          >
            Send
          </button>
          <button
            className="px-3 py-1.5 rounded bg-sky-700/80 hover:bg-sky-700 disabled:opacity-50"
            onClick={() => run(forceSend, "Force Send (bundle)")}
            disabled={busy || !hasWorkspace}
          >
            Force Send
          </button>
          <button
            className="px-3 py-1.5 rounded bg-purple-600/80 hover:bg-purple-600 disabled:opacity-50"
            onClick={() => run(reload, "Receive (bundle)")}
            disabled={busy || !hasWorkspace}
          >
            Receive
          </button>
        </div>
      </section>

      <section className="mb-6 rounded-lg border border-white/10 p-4 bg-white/5">
        <h2 className="font-medium mb-3">Backups</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            className="px-3 py-1.5 rounded bg-amber-600/80 hover:bg-amber-600 disabled:opacity-50"
            onClick={() => run(exportAll, "Exporting backup")}
            disabled={busy}
          >
            Export Backup
          </button>
          <button
            className="px-3 py-1.5 rounded bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            Import Backup…
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleImportFile}
          />
        </div>
      </section>

      <section className="mb-6 rounded-lg border border-white/10 p-4 bg-white/5">
        <h2 className="font-medium mb-3">Health</h2>
        <button
          className="px-3 py-1.5 rounded bg-teal-600/80 hover:bg-teal-600 disabled:opacity-50"
          onClick={() => run(healthCheck, "Health check")}
          disabled={busy}
        >
          Run Health Check
        </button>
      </section>

      {msg ? (
        <pre className="whitespace-pre-wrap text-xs opacity-80 p-3 rounded bg-black/30 border border-white/10">
          {msg}
        </pre>
      ) : null}
    </div>
  );
}
